import { useMemo } from 'react';

import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';
import { Table_NEW } from '@/components/custom/organisms/Table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AirtableBase } from "@/types/crm/airtable/common";

type Props = {
    show: boolean;
    onClose: () => void;
    bases: { name: string, id: string }[] | [];
    onSelectBase: (base: AirtableBase) => void;
}

const SelectBaseModal: React.FC<Props> = ({
    show, 
    onClose,
    bases,
    onSelectBase
}) => {
    const onSelectBaseHandler = (baseId: string) => {
        const base = bases?.find(base => base.id === baseId);
        if(!base) return;
        onSelectBase(base as AirtableBase);
    }

    const tableData = useMemo(() => {
        return bases;
    }, [bases]);
    const columnHelper = createColumnHelper<{id: string; name: string | null;}>();
    const defaultColumns = [
        columnHelper.display({
            id: 'question',
            header: () => (
                <div className='flex items-center'>
                    <i className={`table-header__text-icon bi bi-file-person-fill me-2 fs-20`}></i>
                    <span className='table-header__text'>Base Name</span>
                </div>
            ),
            cell: (params) => (
                <div className="flex">
                    {params.row.original.name}
                </div>
            )
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <div className='flex items-center'>
                    <i className={`table-header__text-icon bi bi-gear-fill me-2 fs-20`}></i>
                    <span className='table-header__text'>Actions</span>
                </div>
            ),
            cell: (params) => (
                <TooltipProvider>
                    <div className="flex justify-center w-full gap-1" >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    size='action'
                                    className={`text-nowrap flex text-sm`}
                                    onClick={() => onSelectBaseHandler(params.row.original.id)}
                                >
                                    <i className="bi bi-plus-lg"></i>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Select</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </TooltipProvider>
            ),
            size: 100,
            maxSize: 100,
        }),
    ]

    const table = useReactTable({
        columns: defaultColumns,
        data: tableData ?? [],
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <>
            <Dialog open={show} onOpenChange={onClose} defaultOpen={false}>
                <DialogContent className="max-w-6xl">
                    <DialogHeader>
                        <DialogTitle>
                            <div className="p-0 px-1 m-0 text-start fw-bold">
                               Select a Base
                            </div>
                        </DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] pe-3">
                        <div className="w-full h-full max-h-80 overflow-scroll-y wissp-scroll-box pe-2 grow">
                            <Table_NEW table={table} />
                        </div>
                    </ScrollArea>

                    <div className="flex justify-end w-full">
                        <div className="w-full mb-1 sm:mb-0 sm:me-2 sm:w-auto">
                            <Button variant="secondary" type="button" onClick={onClose}>Close</Button>
                        </div>
                    </div>

                </DialogContent>
            </Dialog>
        </>
    )
}

export { SelectBaseModal };
